import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  normalizeJoinCode,
  isJoinFailure,
  type JoinHuntResponse,
} from '../hunts/useJoinHuntByCode'

/**
 * Normalized team join code. Team codes (e.g. "DNI251") differ from hunt codes
 * (NNNLLL); we don't hard-validate the exact team format here (it's an open
 * question on CLUB-176) — we normalize (trim + uppercase) and let the backend
 * reject an unknown/expired code. We only guard against an empty submission.
 */
export function isNonEmptyCode(raw: string): boolean {
  return normalizeJoinCode(raw).length > 0
}

/**
 * Join a team by its join code (CLUB-177). Reuses the `joinWithCode` Firebase
 * callable (europe-west1) — the same unified join entry point the hunt-join flow
 * uses; it routes by the code. On success the my-groups list is invalidated so
 * the joined team appears in the Teams section.
 *
 * NOTE for backend: this assumes `joinWithCode` also handles team/group codes.
 * If team joining is served by a distinct callable, only the callable name here
 * needs to change — the UI/contract (a `{ status }` envelope) stays the same.
 */
export function useJoinTeamByCode() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (rawCode: string): Promise<void> => {
      const code = normalizeJoinCode(rawCode)
      const [{ getApp }, { getFunctions, httpsCallable }] = await Promise.all([
        import('firebase/app'),
        import('firebase/functions'),
      ])
      const fns = getFunctions(getApp(), 'europe-west1')
      const call = httpsCallable<{ joinCode: string }, JoinHuntResponse>(fns, 'joinWithCode')
      const res = await call({ joinCode: code })
      if (isJoinFailure(res.data)) {
        throw new Error(res.data?.message ?? 'That code didn’t work. Check it and try again.')
      }
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['clubhouse', 'me', 'groups'] })
    },
  })
}
